import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';
const REFRESH_MS = 30_000;

interface HeatmapCell { dow: number; hour: number; total_hits: number; }
interface TopUser { user_id: string; display_name: string | null; total_hits: number; dominant_cap: string; most_recent: string; }
interface HitsPoint { ts: string; total: number; }
interface VertexPoint { ts: string; rpm: number; tpm: number; rpd: number; util_pct: number; }
interface TimingKpis {
    queue_avg: number; queue_p95: number; queue_count: number;
    pickup_avg: number; pickup_p95: number; pickup_count: number;
    exec_avg: number; exec_p95: number; exec_count: number;
    total_avg: number; total_p95: number; total_count: number;
}
interface SlowCustomer { user_id: string; display_name: string | null; job_count: number; avg_total_sec: number; p95_total_sec: number; dominant_stage: string; }
interface SlowJob { job_id: string; user_id: string; file_name: string; type: string; created_at: string; queue_sec: number | null; pickup_sec: number | null; exec_sec: number | null; total_sec: number; }
interface TimingHourPoint { hour_bucket: string; median_total_sec: number; job_count: number; }

interface CapAnalytics {
    heatmap: HeatmapCell[];
    top_users: TopUser[];
    hits_over_time: HitsPoint[];
    vertex_timeline: VertexPoint[];
    realtime_queue: { pending: number; running: number };
    timing_kpis: TimingKpis | null;
    slow_customers: SlowCustomer[];
    slow_jobs: SlowJob[];
    timing_hourly: TimingHourPoint[];
    generated_at: string;
}

const DOW_LABELS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

const CapAnalyticsTab: React.FC = () => {
    const { session } = useAuth();
    const [data, setData] = useState<CapAnalytics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!session?.access_token) return;
        try {
            const res = await fetch(`${WORKERS_URL}/api/admin/cap-analytics`, {
                headers: { 'Authorization': `Bearer ${session.access_token}` },
            });
            if (!res.ok) {
                const e = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
                setError((e as any).error || 'Failed');
                return;
            }
            setData(await res.json() as CapAnalytics);
            setError(null);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [session?.access_token]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, REFRESH_MS);
        return () => clearInterval(interval);
    }, [fetchData]);

    if (loading && !data) return <div className="p-8 text-slate-400">Đang tải dữ liệu...</div>;
    if (error) return <div className="p-8 text-red-600">Lỗi: {error}</div>;
    if (!data) return <div className="p-8 text-slate-400">Chưa có dữ liệu</div>;

    const heatmapMatrix: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    let maxHits = 0;
    for (const c of data.heatmap) {
        if (c.dow >= 0 && c.dow < 7 && c.hour >= 0 && c.hour < 24) {
            heatmapMatrix[c.dow][c.hour] = c.total_hits;
            if (c.total_hits > maxHits) maxHits = c.total_hits;
        }
    }
    const cellOpacity = (n: number) => maxHits > 0 ? Math.min(1, 0.1 + (n / maxHits) * 0.9) : 0;

    const maxHitOverTime = Math.max(1, ...data.hits_over_time.map(p => p.total));
    const maxVertexUtil = Math.max(1, ...data.vertex_timeline.map(p => p.util_pct));

    return (
        <div className="p-6 overflow-y-auto bg-slate-50 h-full">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-slate-800">📊 Cap Analytics</h2>
                <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500">Cập nhật: {new Date(data.generated_at).toLocaleString('vi-VN')}</span>
                    <button onClick={fetchData} className="px-3 py-1 bg-indigo-600 text-white rounded text-xs font-bold hover:bg-indigo-700">
                        Refresh
                    </button>
                </div>
            </div>

            {/* Section 5 — Real-time queue (top of page for visibility) */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4 grid grid-cols-2 gap-4">
                <div>
                    <div className="text-xs text-slate-400 uppercase font-bold">Pending Jobs</div>
                    <div className="text-3xl font-bold text-amber-600 font-mono">{data.realtime_queue.pending}</div>
                </div>
                <div>
                    <div className="text-xs text-slate-400 uppercase font-bold">Running Jobs</div>
                    <div className="text-3xl font-bold text-blue-600 font-mono">{data.realtime_queue.running}</div>
                </div>
            </div>

            {/* Section 1 — Heatmap 7×24 */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                <div className="text-sm font-bold text-slate-700 mb-2">Heatmap rejection — 30 ngày qua</div>
                <div className="text-[10px] text-slate-400 mb-2">Mỗi ô = SUM(hit_count) theo (ngày trong tuần × giờ trong ngày)</div>
                <div className="overflow-x-auto">
                    <div className="inline-block">
                        <div className="flex">
                            <div className="w-8" />
                            {Array.from({ length: 24 }).map((_, h) => (
                                <div key={h} className="w-5 text-[8px] text-slate-400 text-center font-mono">{h}</div>
                            ))}
                        </div>
                        {DOW_LABELS.map((label, dow) => (
                            <div key={dow} className="flex">
                                <div className="w-8 text-[10px] text-slate-500 font-bold flex items-center">{label}</div>
                                {Array.from({ length: 24 }).map((_, h) => {
                                    const v = heatmapMatrix[dow][h];
                                    return (
                                        <div
                                            key={h}
                                            className="w-5 h-5 border border-slate-100"
                                            style={{ backgroundColor: v > 0 ? `rgba(220, 38, 38, ${cellOpacity(v)})` : '#f8fafc' }}
                                            title={`${label} ${h}h: ${v} hits`}
                                        />
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="text-[10px] text-slate-400 mt-1">Max: {maxHits} hits</div>
            </div>

            {/* Section 2 — Top Users */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                <div className="text-sm font-bold text-slate-700 mb-2">Top 20 users hit cap — 7 ngày qua</div>
                {data.top_users.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">Chưa có user nào bị reject</div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="text-[10px] text-slate-400 uppercase">
                            <tr>
                                <th className="text-left py-1">User</th>
                                <th className="text-right py-1">Total hits</th>
                                <th className="text-left py-1 pl-3">Dominant cap</th>
                                <th className="text-right py-1">Last seen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_users.map((u) => (
                                <tr key={u.user_id} className="border-t border-slate-100">
                                    <td className="py-1.5 font-mono">{u.display_name || u.user_id.slice(0, 8)}</td>
                                    <td className="py-1.5 text-right font-bold text-red-600">{u.total_hits}</td>
                                    <td className="py-1.5 pl-3"><span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-mono">{u.dominant_cap}</span></td>
                                    <td className="py-1.5 text-right text-slate-500 text-[10px]">{new Date(u.most_recent).toLocaleString('vi-VN')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Section 3 — Hits over time */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                <div className="text-sm font-bold text-slate-700 mb-2">Hits over time — 24h qua (60 điểm)</div>
                <div className="flex items-end gap-px h-24">
                    {data.hits_over_time.map((p, i) => (
                        <div
                            key={i}
                            className="flex-1 bg-red-500 hover:bg-red-700 transition-colors"
                            style={{ height: `${(p.total / maxHitOverTime) * 100}%` }}
                            title={`${new Date(p.ts).toLocaleTimeString('vi-VN')}: ${p.total} hits`}
                        />
                    ))}
                </div>
            </div>

            {/* Section 4 — Vertex utilization timeline */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                <div className="text-sm font-bold text-slate-700 mb-2">Vertex utilization — 24h qua</div>
                <div className="flex items-end gap-px h-24">
                    {data.vertex_timeline.map((p, i) => (
                        <div
                            key={i}
                            className={`flex-1 ${p.util_pct > 80 ? 'bg-red-500' : p.util_pct > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ height: `${Math.min(100, (p.util_pct / Math.max(maxVertexUtil, 1)) * 100)}%` }}
                            title={`${new Date(p.ts).toLocaleTimeString('vi-VN')}: ${p.util_pct.toFixed(1)}% RPM (${p.rpm}/3000)`}
                        />
                    ))}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">Cap: 3000 RPM (75% của Vertex Tier 1 4K)</div>
            </div>

            {/* US-04-11 Section 5 — Stage Breakdown KPIs (4 cards) */}
            {data.timing_kpis && (
                <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                    <div className="text-sm font-bold text-slate-700 mb-2">⏱ Job timing breakdown — 24h qua</div>
                    <div className="text-[10px] text-slate-400 mb-3">Mỗi stage: avg / p95 / count. Total = end-to-end user wait.</div>
                    <div className="grid grid-cols-4 gap-3">
                        {([
                            { key: 'queue', label: 'Queue', desc: 'created → queued (dispatcher)' },
                            { key: 'pickup', label: 'Pickup', desc: 'queued → started (Worker spawn)' },
                            { key: 'exec', label: 'Exec', desc: 'started → completed (Gemini)' },
                            { key: 'total', label: 'Total', desc: 'created → completed' },
                        ] as const).map((s) => {
                            const k = data.timing_kpis as any;
                            const avg = k[`${s.key}_avg`] ?? 0;
                            const p95 = k[`${s.key}_p95`] ?? 0;
                            const cnt = k[`${s.key}_count`] ?? 0;
                            const color = s.key === 'total' ? 'text-violet-700' : avg > 60 ? 'text-red-600' : avg > 30 ? 'text-amber-600' : 'text-emerald-600';
                            return (
                                <div key={s.key} className="border border-slate-200 rounded p-2" title={s.desc}>
                                    <div className="text-[10px] uppercase font-bold text-slate-400">{s.label}</div>
                                    <div className={`text-2xl font-mono font-bold ${color}`}>{avg}<span className="text-xs ml-1 text-slate-400">s avg</span></div>
                                    <div className="text-[10px] text-slate-500 font-mono">p95 {p95}s · {cnt} jobs</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* US-04-11 Section 6 — Top 10 slow customers (7 days) */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                <div className="text-sm font-bold text-slate-700 mb-2">🐢 Top 10 slow customers — 7 ngày</div>
                {(!data.slow_customers || data.slow_customers.length === 0) ? (
                    <div className="text-xs text-slate-400 italic">Chưa có data 7 ngày</div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="text-[10px] text-slate-400 uppercase">
                            <tr>
                                <th className="text-left py-1">User</th>
                                <th className="text-right py-1">Jobs</th>
                                <th className="text-right py-1">Avg total</th>
                                <th className="text-right py-1">p95 total</th>
                                <th className="text-left py-1 pl-3">Dominant stage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.slow_customers.map((u) => (
                                <tr key={u.user_id} className="border-t border-slate-100">
                                    <td className="py-1.5 font-mono">{u.display_name || u.user_id.slice(0, 8)}</td>
                                    <td className="py-1.5 text-right">{u.job_count}</td>
                                    <td className="py-1.5 text-right font-bold text-red-600">{u.avg_total_sec}s</td>
                                    <td className="py-1.5 text-right text-slate-500">{u.p95_total_sec}s</td>
                                    <td className="py-1.5 pl-3"><span className="px-2 py-0.5 bg-violet-100 text-violet-800 rounded text-[10px] font-mono">{u.dominant_stage}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* US-04-11 Section 7 — Top 10 slow individual jobs */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                <div className="text-sm font-bold text-slate-700 mb-2">🚨 Top 10 slow jobs — 7 ngày (worst outliers)</div>
                {(!data.slow_jobs || data.slow_jobs.length === 0) ? (
                    <div className="text-xs text-slate-400 italic">Chưa có data 7 ngày</div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="text-[10px] text-slate-400 uppercase">
                            <tr>
                                <th className="text-left py-1">File</th>
                                <th className="text-left py-1 pl-2">Type</th>
                                <th className="text-right py-1">Queue</th>
                                <th className="text-right py-1">Pickup</th>
                                <th className="text-right py-1">Exec</th>
                                <th className="text-right py-1 pr-2">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.slow_jobs.map((j) => (
                                <tr key={j.job_id} className="border-t border-slate-100" title={`User ${j.user_id.slice(0, 8)} @ ${new Date(j.created_at).toLocaleString('vi-VN')}`}>
                                    <td className="py-1.5 font-mono truncate max-w-[200px]">{j.file_name || '(unnamed)'}</td>
                                    <td className="py-1.5 pl-2 text-slate-500">{j.type}</td>
                                    <td className="py-1.5 text-right text-slate-500">{j.queue_sec ?? '–'}s</td>
                                    <td className="py-1.5 text-right text-slate-500">{j.pickup_sec ?? '–'}s</td>
                                    <td className="py-1.5 text-right text-slate-700">{j.exec_sec ?? '–'}s</td>
                                    <td className="py-1.5 text-right font-bold text-red-600 pr-2">{j.total_sec}s</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* US-04-11 Section 8 — Hourly trend median total time (last 24h) */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                <div className="text-sm font-bold text-slate-700 mb-2">📈 Median total time — 24h qua (theo giờ)</div>
                {(!data.timing_hourly || data.timing_hourly.length === 0) ? (
                    <div className="text-xs text-slate-400 italic">Chưa có data 24h</div>
                ) : (
                    <>
                        {(() => {
                            const maxMedian = Math.max(1, ...data.timing_hourly.map(p => p.median_total_sec));
                            return (
                                <div className="flex items-end gap-1 h-24">
                                    {data.timing_hourly.map((p, i) => {
                                        const h = Math.round((p.median_total_sec / maxMedian) * 100);
                                        const color = p.median_total_sec > 120 ? 'bg-red-500' : p.median_total_sec > 60 ? 'bg-amber-500' : 'bg-emerald-500';
                                        return (
                                            <div key={i} className="flex-1 flex flex-col items-center">
                                                <div
                                                    className={`w-full ${color} transition-all`}
                                                    style={{ height: `${h}%` }}
                                                    title={`${new Date(p.hour_bucket).toLocaleString('vi-VN')}: median ${p.median_total_sec}s (${p.job_count} jobs)`}
                                                />
                                                <div className="text-[8px] text-slate-400 mt-0.5 font-mono">{new Date(p.hour_bucket).getHours()}h</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                        <div className="text-[10px] text-slate-400 mt-1">Bar height = median total time (s). Spike = peak hour bottleneck.</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CapAnalyticsTab;
